import { useState, useCallback } from 'react';
import { Layout, Card, StepIndicator, Alert } from './components/Layout';
import { FileUploadZone } from './components/FileUploadZone';
import { UserInfoForm } from './components/UserInfoForm';
import { ProcessingIndicator } from './components/ProcessingIndicator';
import { NoticeResults } from './components/NoticeResults';
import { HRSubmissionSuccess } from './components/HRSubmissionSuccess';
import { processNotice, createZendeskTicket, handleApiError } from './utils/api';
import { validateFile } from './utils/validation';
import { HUBSPOT_MEETING_URL } from './config/constants';
import type { 
  UserInfo, 
  ProcessingState,
  TicketCreationState 
} from './types/notice';

type AppStep = 'upload' | 'processing' | 'results' | 'success';

function App() {
  // Main workflow state
  const [currentStep, setCurrentStep] = useState<AppStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  
  // Processing state
  const [processingState, setProcessingState] = useState<ProcessingState>({
    isProcessing: false,
    currentStep: 'idle',
    error: null,
    result: null,
  });

  // Ticket creation state
  const [ticketState, setTicketState] = useState<TicketCreationState>({
    isCreating: false,
    error: null,
    result: null,
  });

  // Main error state
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Step indicator configuration
  const steps = ['Upload Document', 'Processing', 'Review Results', 'Complete'];
  const currentStepIndex = {
    upload: 0,
    processing: 1,
    results: 2,
    success: 3,
  }[currentStep];

  // File selection handler
  const handleFileSelect = useCallback((file: File | null) => {
    if (!file) {
      setSelectedFile(null);
      setGlobalError(null);
      return;
    }

    const validation = validateFile(file);
    if (!validation.valid) {
      setGlobalError(validation.error || 'Invalid file');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setGlobalError(null);
  }, []);

  // Form submission handler
  const handleFormSubmit = useCallback(async (formUserInfo: UserInfo) => {
    if (!selectedFile) {
      setGlobalError('Please select a file first');
      return;
    }

    setUserInfo(formUserInfo);
    setCurrentStep('processing');
    setGlobalError(null);

    // Initialize processing state
    setProcessingState({
      isProcessing: true,
      currentStep: 'uploading',
      error: null,
      result: null,
    });

    try {
      const result = await processNotice(selectedFile, formUserInfo);
      
      setProcessingState({
        isProcessing: false,
        currentStep: 'complete',
        error: null,
        result,
      });

      setCurrentStep('results');
      
    } catch (error) {
      const errorMessage = handleApiError(error);
      
      setProcessingState(prev => ({
        ...prev,
        isProcessing: false,
        currentStep: 'error',
        error: errorMessage,
      }));
      
      setGlobalError(errorMessage);
    }
  }, [selectedFile]);

  // Ticket creation handler
  const handleCreateTicket = useCallback(async (noticeId: string) => {
    setTicketState({
      isCreating: true,
      error: null,
      result: null,
    });

    try {
      const ticketResult = await createZendeskTicket(noticeId);
      
      setTicketState({
        isCreating: false,
        error: null,
        result: ticketResult,
      });

      setCurrentStep('success');
      
    } catch (error) {
      const errorMessage = handleApiError(error);
      
      setTicketState({
        isCreating: false,
        error: errorMessage,
        result: null,
      });
    }
  }, []);

  // Reset handler - keeps user info but clears file
  const handleStartOver = useCallback(() => {
    setCurrentStep('upload');
    setSelectedFile(null);
    // Keep userInfo so form is pre-filled
    setProcessingState({
      isProcessing: false,
      currentStep: 'idle',
      error: null,
      result: null,
    });
    setTicketState({
      isCreating: false,
      error: null,
      result: null,
    });
    setGlobalError(null);
  }, []);

  // Start fresh handler - clears everything including user info
  const handleStartFresh = useCallback(() => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setUserInfo(null);
    setProcessingState({
      isProcessing: false,
      currentStep: 'idle',
      error: null,
      result: null,
    });
    setTicketState({
      isCreating: false,
      error: null,
      result: null,
    });
    setGlobalError(null);
  }, []);

  // Clear global error handler
  const handleClearError = useCallback(() => {
    setGlobalError(null);
  }, []);

  return (
    <Layout>
      <div className="space-y-8">
        {/* Step Indicator - hidden on upload screen */}
        {currentStep !== 'upload' && (
          <StepIndicator currentStep={currentStepIndex} steps={steps} />
        )}

        {/* Global Error Display */}
        {globalError && (
          <Alert type="error" title="Error" onDismiss={handleClearError}>
            {globalError}
          </Alert>
        )}

        {/* Main Content */}
        <div>
          {currentStep === 'upload' && (
            <div className="space-y-8">
              <Card className="fade-in">
                <div className="space-y-8">
                  <FileUploadZone
                    onFileSelect={handleFileSelect}
                    selectedFile={selectedFile}
                    error={globalError}
                  />
                  
                  {selectedFile && (
                    <div className="slide-up">
                      <UserInfoForm
                        onSubmit={handleFormSubmit}
                        initialValues={userInfo || undefined}
                        isSubmitting={processingState.isProcessing}
                      />
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          {currentStep === 'processing' && (
            <div className="fade-in">
              <ProcessingIndicator
                isProcessing={processingState.isProcessing}
                currentStep={processingState.currentStep}
                error={processingState.error}
              />
            </div>
          )}

          {currentStep === 'results' && processingState.result && (
            <div className="fade-in">
              <NoticeResults
                result={processingState.result}
                isCreatingTicket={ticketState.isCreating}
                onCreateTicket={() => handleCreateTicket(processingState.result!.notice_id)}
                onStartOver={handleStartOver}
              />
              
              {/* Ticket Creation Error */}
              {ticketState.error && (
                <div className="mt-6">
                  <Alert type="error" title="Failed to Contact HR Team">
                    <div className="space-y-2">
                      <p>{ticketState.error}</p>
                      <p className="text-sm">
                        Please try again or{' '}
                        <a href={HUBSPOT_MEETING_URL} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline">
                          book a call
                        </a>{' '}
                        with an HR expert.
                      </p>
                    </div>
                  </Alert>
                </div>
              )}
            </div>
          )}

          {currentStep === 'success' && ticketState.result && processingState.result && (
            <div className="fade-in">
              <HRSubmissionSuccess
                ticketResult={ticketState.result}
                noticeResult={processingState.result}
                onStartOver={handleStartOver}
                onStartFresh={handleStartFresh}
              />
            </div>
          )}
        </div>

        {/* Debug Information (Development Only) - NEVER shows in production */}
        {(import.meta.env.DEV && !import.meta.env.PROD && import.meta.env.MODE !== 'production') && (
          <Card className="border-dashed border-yellow-300 bg-yellow-50 print-hide">
            <details className="text-sm">
              <summary className="cursor-pointer font-medium text-yellow-800 mb-2">
                🔧 Debug Information (Development Mode Only)
              </summary>
              <div className="space-y-2 text-yellow-700">
                <p><strong>Environment:</strong> {import.meta.env.MODE}</p>
                <p><strong>Current Step:</strong> {currentStep}</p>
                <p><strong>File Selected:</strong> {selectedFile?.name || 'None'}</p>
                <p><strong>Processing:</strong> {processingState.isProcessing ? 'Yes' : 'No'}</p>
                <p><strong>Creating Ticket:</strong> {ticketState.isCreating ? 'Yes' : 'No'}</p>
                {processingState.result && (
                  <p><strong>Notice ID:</strong> {processingState.result.notice_id}</p>
                )}
                <p className="text-xs border-t pt-2 mt-2 text-yellow-600">
                  This debug panel will never appear in production builds
                </p>
              </div>
            </details>
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default App;